import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import CustomButton from "web/components/form-elements/inputs/CustomButton.component";
import { ConfirmationContext } from "web/context/Confirmation.context";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import MultiSelectModal from "webComponentsFormElements/MultiSelectModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SingleSelectModal from "webComponentsFormElements/SingleSelectModal.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  container: {
    flex: 1,
  },
  tableContainer: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.color.lightGrey,
    borderRadius: 8,
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: theme.color.background,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightGrey,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  headerCell: {
    paddingHorizontal: 4,
    flex: 2,
    minWidth: 100,
  },
  headerText: {
    fontSize: 14,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.backgroundText,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightGrey,
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: "flex-start",
  },
  cellText: {
    fontSize: 14,
    color: theme.color.backgroundText,
  },
  editableCell: {
    backgroundColor: theme.color.lightBlue,
    borderRadius: 4,
    paddingHorizontal: 4,
    minHeight: 32,
    justifyContent: "center",
  },
  deleteHeaderCell: {
    flex: 1,
  },
  deleteCell: {
    flex: 1,
  },
  textInput: {
    fontSize: 14,
    color: theme.color.backgroundText,
    padding: 4,
    minHeight: 32,
    textAlignVertical: "top",
    flexWrap: "wrap",
    maxWidth: "100%",
  },
  multiSelectContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
  },
  tag: {
    backgroundColor: theme.color.blue,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  tagText: {
    fontSize: 12,
    color: theme.color.white,
  },
  addButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: theme.color.blue,
    borderStyle: "dashed",
  },
  cellContainer: {
    flex: 2,
    minHeight: 32,
    minWidth: 100,
  },
  addButtonText: {
    fontSize: 12,
    color: theme.color.blue,
  },
  selectionDisplay: {
    flexShrink: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2,
    alignItems: "center",
    overflowX: "hidden",
  },
  selectionTag: {
    backgroundColor: theme.color.lightBlue,
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: theme.color.primary,
    flexShrink: 1,
  },
  selectionTagText: {
    fontSize: 10,
    color: theme.color.primary,
    fontWeight: theme.font.weight.medium,
  },
  clickableText: {
    color: theme.color.primary,
    textDecorationLine: "underline",
  },
  emptyState: {
    textAlign: "center",
    color: theme.color.darkGrey,
    fontStyle: "italic",
    paddingVertical: 20,
  },
  searchBar: {
    padding: 0,
    margin: 0,
    flex: "none",
    height: 40,
  },
  addNewButtonContainer: {
    marginBottom: 8,
    marginTop: 4,
    alignSelf: "flex-start",
    display: "flex",
  },
  addNewButtonText: {
    fontSize: 14,
    color: theme.color.primary,
    fontWeight: theme.font.weight.semibold,
  },
  buttonGroup: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  deleteButtonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  cellPressable: {
    flex: 1,
    minWidth: 0,
  },
  cell: {
    flexDirection: "row",
  },
  newRow: {
    border: `2px solid ${theme.color.primary}`,
  },
  button: {
    marginTop: 0,
    marginBottom: 0,
    padding: 0,
  },
  errorPressable: {
    borderColor: theme.color.red,
    borderWidth: 1,
    borderRadius: 4,
  },
  errorStyleText: {
    color: theme.color.red,
    fontSize: 10,
    marginBottom: 2,
  },
});

export default function InlineEditableTable({
  data = [],
  columns = [],
  searchPlaceholder = "Search...",
  onRowUpdate,
  onRemoveContact,
}) {
  const {
    locationList,
    organizationSystems,
    otherContactList,
    organizationColor,
  } = useContext(OrganizationContext);
  const [renderData, setRenderData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchText, setSearchText] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [modalState, setModalState] = useState({
    visible: false,
    type: null,
    column: null,
    rowId: null,
    currentValue: null,
  });
  const [systemsFilter, setSystemsFilter] = useState([]);
  const [showSystemsFilterModal, setShowSystemsFilterModal] = useState(false);
  const { showConfirmation } = useContext(ConfirmationContext);
  const [cellErrorMap, setCellErrorMap] = useState({});

  const requiredColumns = useMemo(
    () =>
      columns
        .filter((col) => col.type === "textRequired")
        .map((col) => ({ key: col.key, errorMessage: col.errorMessage })),
    [columns],
  );

  useEffect(() => {
    startTransition(() => setRenderData(data));
  }, [data]);

  const sortedData = useMemo(() => {
    // Separate new rows from existing rows
    const newRows = renderData.filter((row) => row.id === "new" || row.isNew);
    const existingRows = renderData.filter(
      (row) => row.id !== "new" && !row.isNew,
    );

    // Apply sorting only to existing rows
    let sortedExistingRows = existingRows;
    if (sortConfig.key) {
      const col = columns.find((clm) => clm.key === sortConfig.key);
      if (col && col.sortable) {
        sortedExistingRows = [...existingRows].sort((a, b) => {
          // Use custom sort function if provided
          if (col.customSort) {
            const result = col.customSort(a, b);
            return sortConfig.direction === "asc" ? result : -result;
          }

          // Default string sorting
          const aVal = a[sortConfig.key] || "";
          const bVal = b[sortConfig.key] || "";
          if (typeof aVal === "string" && typeof bVal === "string") {
            if (aVal.localeCompare(bVal) === 0) {
              return 0;
            }
            return sortConfig.direction === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }
          return 0;
        });
      }
    }

    // Return new rows at the top, followed by sorted existing rows
    return [...newRows, ...sortedExistingRows];
  }, [renderData, sortConfig, columns]);

  // Filter by systemsFilter
  const systemsFilteredData = useMemo(() => {
    if (!systemsFilter || systemsFilter.length === 0) {
      return sortedData;
    }
    return sortedData.filter((row) => {
      // Check for systems array in row
      if (Array.isArray(row.systems)) {
        return row.systems.some((id) => systemsFilter.includes(id));
      }
      // Check for _rawContact._associations.Systems
      if (
        row._rawContact &&
        row._rawContact._associations &&
        Array.isArray(row._rawContact._associations.Systems)
      ) {
        return row._rawContact._associations.Systems.some((sys) =>
          systemsFilter.includes(sys.organizationSystemId),
        );
      }
      return false;
    });
  }, [sortedData, systemsFilter]);

  const filteredData = useMemo(() => {
    if (!searchText) {
      return systemsFilteredData;
    }
    return systemsFilteredData.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase()),
      ),
    );
  }, [systemsFilteredData, searchText]);

  const handleCellEdit = useCallback(
    (rowId, columnKey, currentValue) => {
      const column = columns.find((col) => col.key === columnKey);
      if (!column) {
        return;
      }
      const mode = rowId === "new" ? "new" : "edit";
      if (
        column.type === "text" ||
        column.type === "textValidation" ||
        column.type === "textRequired"
      ) {
        setEditingCell(`${rowId}-${columnKey}`);
        setTempValues({ [columnKey]: currentValue });
      } else {
        const modalType = column.type === "contacts" ? "single" : "multi";

        setModalState({
          visible: true,
          type: modalType,
          column,
          rowId,
          currentValue,
          mode,
        });
      }
    },
    [columns],
  );

  const clearErrors = useCallback((rowId, columnKey) => {
    setCellErrorMap((prev) => {
      const newMap = { ...prev };
      delete newMap[`${rowId}-${columnKey}`];
      return newMap;
    });
  }, []);

  const handleCellSave = useCallback(
    (rowId, columnKey, newValue, column) => {
      if (column.type === "textValidation" || column.type === "textRequired") {
        // Handle required text: disallow empty values.
        if (column.type === "textRequired" && !newValue) {
          setCellErrorMap((prev) => ({
            ...prev,
            [`${rowId}-${columnKey}`]:
              column.errorMessage || "This field is required",
          }));
          setEditingCell(null);
          return;
        }

        if (column.type === "textValidation" && !column.validationRegex) {
          if (!column.validationFn) {
            throw new Error(
              "validationRegex or validationFn is required for textValidation type on column definition",
            );
          }
        }

        if (column.type === "textValidation") {
          // Only validate non-empty values; allow clearing unless otherwise constrained.
          if (newValue) {
            let isValid = true;
            if (column.validationFn) {
              isValid = column.validationFn(newValue);
            } else if (column.validationRegex) {
              const validationRegex = new RegExp(column.validationRegex);
              isValid = validationRegex.test(newValue);
            }
            if (!isValid) {
              setCellErrorMap((prev) => ({
                ...prev,
                [`${rowId}-${columnKey}`]:
                  column.errorMessage || "Invalid input",
              }));
              setEditingCell(null);
              return;
            } else {
              clearErrors(rowId, columnKey);
            }
          } else {
            clearErrors(rowId, columnKey);
          }
        } else {
          // textRequired with a non-empty value: clear any previous errors.
          clearErrors(rowId, columnKey);
        }
      }
      if (onRowUpdate) {
        onRowUpdate(rowId, { [columnKey]: newValue });
      }
      setEditingCell(null);
      setTempValues({});
    },
    [onRowUpdate, clearErrors],
  );

  const setRequiredFieldErrors = useCallback(
    (rowId, rowData) => {
      setCellErrorMap((prev) => {
        const newMap = { ...prev };
        requiredColumns.forEach((col) => {
          const value = rowData[col.key];
          if (!value || String(value).trim() === "") {
            newMap[`${rowId}-${col.key}`] =
              col.errorMessage || "This field is required";
          } else {
            delete newMap[`${rowId}-${col.key}`];
          }
        });
        return newMap;
      });
    },
    [requiredColumns],
  );

  const handleNewRowSave = useCallback(
    (rowId, rowData) => {
      // Only check for errors on the current row being saved
      const rowErrors = Object.keys(cellErrorMap).filter((key) =>
        key.startsWith(`${rowId}-`),
      );
      const missingRequiredFields = Boolean(
        requiredColumns.filter((col) => {
          const value = rowData[col.key];
          return !value || String(value).trim() === "";
        }).length,
      );
      if (rowErrors.length > 0 || missingRequiredFields) {
        missingRequiredFields && setRequiredFieldErrors(rowId, rowData);
        return;
      }
      if (onRowUpdate) {
        const updates = { ...rowData };
        delete updates.id;
        delete updates._rawContact;
        delete updates.isNew;
        onRowUpdate(rowId, updates);
      }
      setEditingCell(null);
      setTempValues({});
    },
    [onRowUpdate, cellErrorMap, requiredColumns, setRequiredFieldErrors],
  );

  const handleDeleteRow = useCallback(
    (id) => {
      onRemoveContact(id);
      setTempValues({});
      setCellErrorMap({});
    },
    [onRemoveContact],
  );

  const handleNewCellSave = useCallback(
    (id, columnKey, newValue, column) => {
      if (
        (column.type === "textValidation" || column.type === "textRequired") &&
        newValue
      ) {
        if (
          column.type === "textValidation" &&
          !column.validationRegex &&
          !column.validationFn
        ) {
          throw new Error(
            "validationRegex or validationFn is required for textValidation type on column definition",
          );
        }
        if (column.type === "textValidation") {
          let isValid = true;
          if (column.validationFn) {
            isValid = column.validationFn(newValue);
          } else if (column.validationRegex) {
            const validationRegex = new RegExp(column.validationRegex);
            isValid = validationRegex.test(newValue);
          }
          if (!isValid) {
            setCellErrorMap((prev) => ({
              ...prev,
              [`${id}-${columnKey}`]: column.errorMessage || "Invalid input",
            }));
            setEditingCell(null);
          } else {
            clearErrors(id, columnKey);
          }
        } else {
          clearErrors(id, columnKey);
        }
      }

      setRenderData((prev) => {
        const updatedData = prev.map((row) => {
          if (row.id === id) {
            return { ...row, [columnKey]: newValue };
          }
          return row;
        });
        return updatedData;
      });

      setEditingCell(null);
      setTempValues({});
    },
    [clearErrors],
  );

  const handleModalSave = useCallback(
    (newValue) => {
      if (onRowUpdate && modalState.rowId && modalState.column) {
        modalState.mode === "new"
          ? handleNewCellSave(
              modalState.rowId,
              modalState.column.key,
              newValue,
              modalState.column,
            )
          : onRowUpdate(modalState.rowId, {
              [modalState.column.key]: newValue,
            });
      }
      setModalState({
        visible: false,
        type: null,
        column: null,
        rowId: null,
        currentValue: null,
        mode: null,
      });
    },
    [onRowUpdate, modalState, handleNewCellSave],
  );

  const handleModalClose = useCallback(() => {
    setModalState({
      visible: false,
      type: null,
      column: null,
      rowId: null,
      currentValue: null,
      mode: null,
    });
  }, []);

  const getDropdownOptions = useCallback(
    (column) => {
      let options = [];

      switch (column.type) {
        case "locations":
          options = locationList || [];
          break;
        case "systems":
          options = organizationSystems || [];
          break;
        case "contacts":
          options = (otherContactList || []).map((contact) => ({
            ...contact,
            displayName: `${contact.name}`.trim(),
          }));
          break;
        default:
          options = [];
      }

      return options;
    },
    [locationList, organizationSystems, otherContactList],
  );

  const feedbackMessageStyles = useMemo(
    () => ({
      backgroundColor: `${organizationColor}15`, // 15% opacity
      padding: 12,
      marginBottom: 8,
      borderRadius: 6,
      borderLeftWidth: 4,
      borderLeftColor: `${organizationColor}80`, // 50% opacity
    }),
    [organizationColor],
  );

  const feedbackMessageTextStyles = useMemo(
    () => ({
      color: `${organizationColor}CC`, // 80% opacity
      fontSize: 14,
      fontWeight: theme.font.weight.medium,
    }),
    [organizationColor],
  );

  const renderCell = useCallback(
    (row, column) => {
      if (column.type === "action") {
        return (
          <View style={classes.deleteButtonContainer}>
            <CustomButton
              testID={`delete-contact-${row.id}`}
              onPress={() => {
                showConfirmation({
                  prompt: "Are you sure you want to delete this contact?",
                  onConfirm: () => handleDeleteRow(row.id),
                });
              }}
              styleOverride={classes.button}
              icon="delete-outline"
              variant="icon"
              iconColor={theme.color.red}
              iconSizeOverride={25}
            />
          </View>
        );
      }
      const cellKey = `${row.id}-${column.key}`;
      const isEditing = editingCell === cellKey;
      const currentValue = isEditing
        ? (tempValues[column.key] ?? row[column.key])
        : row[column.key];
      const isNameField = column.key === "name" || column.key === "firstName";
      const isNewContact = row.id === "new";
      const nameIsSet =
        (!!row.name && row.name.trim() !== "") ||
        (!!row.firstName && row.firstName.trim() !== "");
      const otherFieldEditable =
        column.editable && (!isNewContact || isNameField || nameIsSet);

      if (isEditing) {
        return (
          <View style={[classes.cell, classes.editableCell]}>
            <TextInput
              style={[classes.textInput]}
              value={currentValue}
              onChangeText={(text) => {
                if (column.isPhone) {
                  const digits = text.replaceAll(/\D/g, "");
                  let formatted = digits;
                  if (digits.length > 6) {
                    formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
                  } else if (digits.length > 3) {
                    formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                  }
                  setTempValues({ ...tempValues, [column.key]: formatted });
                } else {
                  setTempValues({ ...tempValues, [column.key]: text });
                }
              }}
              onBlur={() =>
                isNewContact
                  ? handleNewCellSave(row.id, column.key, currentValue, column)
                  : handleCellSave(row.id, column.key, currentValue, column)
              }
              onSubmitEditing={() =>
                isNewContact
                  ? handleNewCellSave(row.id, column.key, currentValue, column)
                  : handleCellSave(row.id, column.key, currentValue, column)
              }
              multiline
              autoFocus
            />
          </View>
        );
      }

      let displayValue = currentValue;
      if (column.type === "locations" || column.type === "systems") {
        let currentSelections = [];
        if (isNewContact) {
          const selectedIds = Array.isArray(row[column.key])
            ? row[column.key]
            : [];
          const options = getDropdownOptions(column);
          currentSelections = options.filter((opt) =>
            selectedIds.includes(opt[column.idField]),
          );
        } else {
          const rawContact = row._rawContact;
          if (column.type === "locations") {
            currentSelections = rawContact?._associations?.Locations || [];
          } else if (column.type === "systems") {
            currentSelections = rawContact?._associations?.Systems || [];
          }
        }

        if (currentSelections.length > 0) {
          return (
            <PressableContainer
              style={classes.cell}
              onPress={() =>
                otherFieldEditable &&
                handleCellEdit(row.id, column.key, row[column.key])
              }
            >
              <View style={classes.selectionDisplay}>
                {currentSelections.map((item) => (
                  <View key={item[column.idField]} style={classes.selectionTag}>
                    <Text style={classes.selectionTagText} ellipsizeMode="tail">
                      {item[column.displayField]}
                    </Text>
                  </View>
                ))}
              </View>
            </PressableContainer>
          );
        } else {
          displayValue = otherFieldEditable
            ? "Click to add..."
            : "Click to edit...";
        }
      } else if (column.type === "contacts") {
        if (currentValue) {
          const rawContact = row._rawContact;
          const backupContact = rawContact?._associations?.BackupContact;

          if (backupContact) {
            displayValue = `${backupContact.name}`;
          } else {
            const options = getDropdownOptions(column);
            const selectedItem = options.find(
              (option) => option[column.idField] === currentValue,
            );
            if (selectedItem) {
              displayValue = `${selectedItem.name}`;
            } else {
              displayValue = "Contact selected";
            }
          }
        } else {
          displayValue = isNewContact
            ? "Save before adding backup."
            : "Click to add...";
        }
      } else if (isNameField && isNewContact) {
        displayValue =
          currentValue ||
          (column.key === "firstName"
            ? "Input first name first."
            : "Input a name first.");
      }
      const showButtonGroup = isNameField && isNewContact && nameIsSet;
      const isError = Boolean(cellErrorMap[cellKey]);
      return (
        <View style={classes.cell}>
          {showButtonGroup && (
            <View style={classes.buttonGroup}>
              <CustomButton
                onPress={() => handleNewRowSave(row.id, row)}
                styleOverride={classes.button}
                icon="content-save-outline"
                variant="icon"
                iconSizeOverride={25}
              />
            </View>
          )}
          <View style={classes.cellPressable}>
            <PressableContainer
              onPress={() => {
                if (
                  otherFieldEditable &&
                  !(column.type === "contacts" && isNewContact)
                ) {
                  handleCellEdit(row.id, column.key, row[column.key]);
                }
              }}
              style={isError ? classes.errorPressable : null}
            >
              {isError && (
                <Text style={classes.errorStyleText}>
                  {cellErrorMap[cellKey]}
                </Text>
              )}
              <Text
                style={[
                  classes.cellText,
                  column.editable &&
                    (displayValue === "Click to add..." ||
                      displayValue === "Click to select...") &&
                    classes.clickableText,
                ]}
                ellipsizeMode="tail"
              >
                {displayValue || "Click to edit..."}
              </Text>
            </PressableContainer>
          </View>
        </View>
      );
    },
    [
      editingCell,
      tempValues,
      handleCellEdit,
      handleCellSave,
      getDropdownOptions,
      handleNewCellSave,
      handleNewRowSave,
      handleDeleteRow,
      showConfirmation,
      cellErrorMap,
    ],
  );

  return (
    <View style={classes.container}>
      <SearchBar
        value={searchText}
        onChangeText={setSearchText}
        placeholder={searchPlaceholder}
        style={classes.searchBar}
      />
      {renderData.some((row) => row.id === "new") && (
        <View style={feedbackMessageStyles}>
          <Text style={feedbackMessageTextStyles}>
            When adding a new table entry, start with the fields that have a *
          </Text>
        </View>
      )}
      <ScrollView style={classes.tableContainer}>
        <View>
          <View style={classes.headerRow}>
            {columns.map((column) => {
              const isSortable = column.sortable;
              const isSorted = sortConfig.key === column.key;
              if (column.type === "systems") {
                return (
                  <View
                    key={column.key}
                    style={
                      column.type === "action"
                        ? classes.deleteHeaderCell
                        : classes.headerCell
                    }
                  >
                    <View
                      style={{ flexDirection: "row", alignItems: "center" }}
                    >
                      <Text style={classes.headerText} numberOfLines={1}>
                        {column.label}
                        {column.type === "textRequired" ? " *" : ""}
                      </Text>
                      <PressableContainer
                        style={{ marginLeft: 4, padding: 2 }}
                        onPress={() => setShowSystemsFilterModal(true)}
                      >
                        <Icon
                          name="filter"
                          size={18}
                          color={
                            systemsFilter && systemsFilter.length > 0
                              ? theme.color.primary
                              : theme.color.backgroundText
                          }
                        />
                      </PressableContainer>
                    </View>
                  </View>
                );
              }
              // Default header rendering for other columns
              return (
                <View
                  key={column.key}
                  style={
                    column.type === "action"
                      ? classes.deleteHeaderCell
                      : classes.headerCell
                  }
                >
                  <PressableContainer
                    disabled={!isSortable}
                    onPress={() => {
                      if (!isSortable) {
                        return;
                      }
                      setSortConfig((prev) => {
                        if (prev.key === column.key) {
                          return {
                            key: column.key,
                            direction:
                              prev.direction === "asc" ? "desc" : "asc",
                          };
                        }
                        return { key: column.key, direction: "asc" };
                      });
                    }}
                  >
                    <View
                      style={{ flexDirection: "row", alignItems: "center" }}
                    >
                      <Text style={classes.headerText} numberOfLines={1}>
                        {column.label}
                        {column.type === "textRequired" ? " *" : ""}
                      </Text>
                      {isSortable && (
                        <View style={{ marginLeft: 4 }}>
                          <Icon
                            name={
                              isSorted
                                ? sortConfig.direction === "asc"
                                  ? "chevron-up"
                                  : "chevron-down"
                                : "arrow-up-down"
                            }
                            size={18}
                            color={
                              isSorted
                                ? theme.color.primary
                                : theme.color.backgroundText
                            }
                          />
                        </View>
                      )}
                    </View>
                  </PressableContainer>
                </View>
              );
            })}
          </View>
          {filteredData.length > 0 ? (
            filteredData.map((row) => (
              <View
                key={row.id}
                style={[classes.row, row.id === "new" && classes.newRow]}
              >
                {columns.map((column) => (
                  <View
                    key={column.key}
                    style={
                      column.type === "action"
                        ? classes.deleteCell
                        : classes.cellContainer
                    }
                  >
                    {renderCell(row, column)}
                  </View>
                ))}
              </View>
            ))
          ) : (
            <Text style={classes.emptyState}>No data available</Text>
          )}
        </View>
      </ScrollView>

      {modalState.type === "multi" && modalState.column && (
        <MultiSelectModal
          visible={modalState.visible}
          onClose={handleModalClose}
          onSave={handleModalSave}
          title={`Select ${modalState.column.label}`}
          options={getDropdownOptions(modalState.column)}
          selectedIds={
            Array.isArray(modalState.currentValue)
              ? modalState.currentValue
              : []
          }
          idField={modalState.column.idField}
          displayField={modalState.column.displayField}
        />
      )}

      {modalState.type === "single" && modalState.column && (
        <SingleSelectModal
          visible={modalState.visible}
          onClose={handleModalClose}
          onSave={handleModalSave}
          title={`Select ${modalState.column.label}`}
          options={getDropdownOptions(modalState.column).filter(
            (option) => option[modalState.column.idField] !== modalState.rowId,
          )}
          selectedId={modalState.currentValue}
          idField={modalState.column.idField}
          displayField={modalState.column.displayField}
        />
      )}

      {/* Systems Filter Modal */}
      {showSystemsFilterModal && (
        <MultiSelectModal
          visible={showSystemsFilterModal}
          onClose={() => setShowSystemsFilterModal(false)}
          onSave={(selectedIds) => {
            setSystemsFilter(selectedIds);
            setShowSystemsFilterModal(false);
          }}
          title="Filter by Systems"
          options={organizationSystems || []}
          selectedIds={systemsFilter}
          idField="organizationSystemId"
          displayField="title"
          showClearButton={true}
          onClear={() => setSystemsFilter([])}
        />
      )}
    </View>
  );
}
